<template>
    <div class="auth__login_form">
        <div class="container">
            <template v-if="isIE11()">
                <div class="columns is-variable">
                    <div class="column">
                        <component-induction-message
                            :title="`This browser is not supported`"
                            :content="`This is to ensure your experience is reliable, fast and secure. <br>To continue, please <a href='https://melearning.zendesk.com/knowledge/articles/360015762657/en-gb?brand_id=360001192697' target='_blank'>switch to a supported browser.</a>`"
                            :content-html="true"
                            :theme="'danger'"
                            :force="true"
                            :close-active="false"
                        ></component-induction-message>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="columns is-variable">
                    <div class="column">
                        <div class="auth__login_form_box">
                            <div class="auth__login_form_box_title">{{ $tc('auth.login.buttons.login', 1) }}</div>
                            <div class="auth__login_form_box_fields">
                                <form-email
                                    :disabled="mfaChallenge || newPasswordChallenge || isStagePassword || isStageSSO"
                                    :label="$t('auth.login.labels.email')"
                                    :name="'login-email'"
                                    :v-validate="'required|email'"
                                    :data-vv-as="'email address'"
                                    @emit-enter="handleSubmit"
                                    v-model="username"
                                />
                                <form-text
                                    v-if="isStagePassword"
                                    :disabled="mfaChallenge || newPasswordChallenge"
                                    :password="true"
                                    :label="$t('auth.login.labels.password')"
                                    :name="'login-password'"
                                    :v-validate="{ required: true, regex: passwordRegex }"
                                    :data-vv-as="'password'"
                                    @emit-enter="handleSubmit"
                                    v-model="password"
                                />
                                <form-text
                                    v-if="mfaChallenge"
                                    ref="mfacode"
                                    :label="$t('auth.login.labels.authenticationCode')"
                                    :name="'login-mfa-code'"
                                    :v-validate="{ required: true, regex: /^[0-9]*$/, length: 6 }"
                                    :v-validate-custom-error="`The authentication code length must be 6 and numeric`"
                                    :data-vv-as="'authentication code'"
                                    @emit-enter="logInMfa"
                                    v-model="mfaCode"
                                />
                                <form-text
                                    v-if="newPasswordChallenge"
                                    ref="newpasswordchallenge"
                                    :password="true"
                                    :label="$t('auth.login.labels.newPassword')"
                                    :name="'login-new-password'"
                                    :v-validate="{ required: true, regex: passwordRegex }"
                                    :data-vv-as="'new password'"
                                    @emit-enter="logInNewPassword"
                                    v-model="newPassword"
                                >Password length must be at least 8 and includes:<br>- lowercase letter<br>- uppercase letter<br>- special character<br>- number
                                </form-text>
                                <form-text
                                    v-if="newPasswordChallenge"
                                    ref="confirmnewpasswordchallenge"
                                    :password="true"
                                    :label="$t('auth.login.labels.confirmPassword')"
                                    :name="'login-confirm-new-password'"
                                    :v-validate="{ required: true, regex: passwordRegex, is: newPassword }"
                                    :data-vv-as="'confirm new password'"
                                    @emit-enter="logInNewPassword"
                                    v-model="newPasswordConfirm"
                                />
                            </div>
                            <template v-if="isStageSSO">
                                <div class="auth__login_form_box_note has-text-centered">
                                    <component-text>Your organisation has enabled single-sign-on.</component-text>
                                    <component-text>Please login using your existing credentials.</component-text>
                                </div>
                            </template>
                            <transition name="fade" mode="out-in">
                                <template v-if="error">
                                    <div class="auth__login_form_box_error">
                                        <component-note :danger="true">{{ error }}</component-note>
                                    </div>
                                </template>
                            </transition>
                            <div class="auth__login_form_box_actions">
                                <component-button
                                    v-if="isStageSSO"
                                    v-for="(option, index) in authOptions"
                                    :key="index"
                                    ref="loginsso"
                                    :theme="'primary'"
                                    :size="'full'"
                                    :to-external="SSOUrl(option.id)"
                                    :name="`auth-login-form-box-sso-login-${option.name}`"
                                >Log in {{ option.name }}</component-button>
                                <component-button
                                    v-if="!mfaChallenge && !newPasswordChallenge && !isStageSSO"
                                    ref="loginsubmit"
                                    :theme="'primary'"
                                    :size="'full'"
                                    :animate-loading="true"
                                    :animate-loading-event-name="'user-login'"
                                    :name="'auth-login-form-box-error-login'"
                                    @click="handleSubmit"
                                >{{ isStageEmail ? 'Next' : 'Log in' }}
                                </component-button>
                                <component-button
                                    v-if="mfaChallenge"
                                    ref="loginsubmitmfa"
                                    :theme="'primary'"
                                    :size="'full'"
                                    :animate-loading="true"
                                    :animate-loading-event-name="'user-login'"
                                    @click="logInMfa"
                                    :name="'auth-login-form-box-error-confirm'"
                                >{{ $t('auth.login.buttons.confirm') }}
                                </component-button>
                                <component-button
                                    v-if="newPasswordChallenge"
                                    ref="loginsubmitnewpassword"
                                    :theme="'primary'"
                                    :size="'full'"
                                    :animate-loading="true"
                                    :animate-loading-event-name="'user-login'"
                                    @click="logInNewPassword"
                                    :name="'auth-login-form-box-error-newpasswordlogin'"
                                >{{ $tc('auth.login.buttons.login', 2) }}
                                </component-button>
                                <component-button
                                    v-if="!mfaChallenge && isStagePassword"
                                    :theme="'primary'"
                                    :inverse="true"
                                    :size="'full'"
                                    :to="{ name: 'auth-reset-password' }"
                                    :name="'auth-login-form-box-error-forgot'"
                                >{{ $t('auth.login.buttons.forgot') }}
                                </component-button>
                                <component-button
                                    v-if="!mfaChallenge && isStageSSO"
                                    :theme="'default'"
                                    :size="'full'"
                                    :name="'auth-login-form-box-log-in-password'"
                                    @click="setStage('password')"
                                >Log in with password</component-button>
                                <component-button
                                    v-if="!mfaChallenge && isStagePassword && authOptions.length"
                                    :theme="'default'"
                                    :size="'full'"
                                    :name="'auth-login-form-box-log-in-sso'"
                                    @click="setStage('sso')"
                                >Log in with SSO</component-button>
                                <component-button
                                    v-if="mfaChallenge || isStageSSO || isStagePassword"
                                    :theme="'default'"
                                    :size="'full'"
                                    @click="loginReset"
                                    :name="'auth-login-form-box-error-loginto'"
                                >Use other email address</component-button>
                            </div>
                            <template v-if="checkIsInvitation">
                                <component-separator/>
                                <div class="auth__login_form_box_tip">
                                    <span>{{ $t('auth.login.separator') }} </span>
                                    <component-button
                                        :theme="'primary'"
                                        :text="true"
                                        :underline="true"
                                        :size="''"
                                        :to="{ name: 'auth-signup', query: checkIsInvitation ? $route.query : {} }"
                                        :name="'auth-login-form-box-tip-signup'"
                                    >{{ $t('auth.login.buttons.signUp') }}
                                    </component-button>
                                </div>
                            </template>
                        </div>
                        <div class="auth__login_form_footer">
                            <div class="columns is-centered">
                                <div class="column is-narrow">
                                    <div class="columns is-variable is-1 is-vcentered">
                                        <div class="column is-narrow">
                                            <component-text>Have a key to sign-up?</component-text>
                                        </div>
                                        <div class="column is-narrow">
                                            <component-button
                                                :name="'auth-login-form-footer-sign-up'"
                                                :text="true"
                                                :to="{ name: 'auth-validate-key' }"
                                            >Click here
                                            </component-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentSeparator from '@/components/default/shared/ComponentSeparator'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { passwordRegex } from '@/variables'
import ComponentInductionMessage from '@/components/default/shared/ComponentInductionMessage'
import ComponentText from '@/components/default/shared/ComponentText'
import FormEmail from '@/components/default/forms/FormEmail'
import api from '@/api'

export default {
  name: 'auth-login-form',
  components: { FormEmail, ComponentText, ComponentInductionMessage, ComponentNote, ComponentSeparator, ComponentButton, FormText },
  data () {
    return {
      passwordRegex
    }
  },
  computed: {
    username: {
      get () {
        return this.$store.getters.getAuthUserLoginUsername
      },
      set (value) {
        this.$store.dispatch('authUserSetLoginUsername', value)
      }
    },
    password: {
      get () {
        return this.$store.getters.getAuthUserLoginPassword
      },
      set (value) {
        this.$store.dispatch('authUserSetLoginPassword', value)
      }
    },
    mfaChallenge () {
      return this.$store.getters.getAuthUserLoginMfaChallenge
    },
    mfaCode: {
      get () {
        return this.$store.getters.getAuthUserLoginMfaCode
      },
      set (value) {
        return this.$store.dispatch('authUserSetLoginMfaCode', value)
      }
    },
    newPasswordChallenge () {
      return this.$store.getters.getAuthUserLoginNewPasswordChallenge
    },
    newPassword: {
      get () {
        return this.$store.getters.getAuthUserLoginNewPassword
      },
      set (value) {
        return this.$store.dispatch('authUserSetLoginNewPassword', value)
      }
    },
    newPasswordConfirm: {
      get () {
        return this.$store.getters.getAuthUserLoginNewPasswordConfirm
      },
      set (value) {
        return this.$store.dispatch('authUserSetLoginNewPasswordConfirm', value)
      }
    },
    error () {
      return this.$store.getters.getAuthUserLoginError
    },
    //
    checkIsInvitation () {
      return Boolean(this.$store.getters.getAuthUserSignUpInvitation)
    },
    isStageEmail () {
      return this.$store.getters.getAuthUserLoginStage.email
    },
    isStagePassword () {
      return this.$store.getters.getAuthUserLoginStage.password
    },
    isStageSSO () {
      return this.$store.getters.getAuthUserLoginStage.sso
    },
    authOptions () {
      return this.$store.getters.getAuthUserLoginAuthOptions
    }
  },
  watch: {
    mfaChallenge (value) {
      if (value === true) {
        setTimeout(() => {
          this.$refs.mfacode.$refs.formtext.focus()
        }, 100)
      }
    }
  },
  methods: {
    SSOUrl (id) {
      return api.subscriptionmanager + '/saml/login/' + id
    },
    handleSubmit () {
      this.$refs.loginsubmit.startLoading()
      this.$validator.resume()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.isStageEmail) {
            this.checkUserLoginMethods().then(response => {
              const authOptions = response

              this.setAuthOptions(authOptions)

              if (authOptions.length) {
                this.setStage('sso')
              } else {
                this.setStage('password')
              }
            }).catch(() => {

            }).finally(() => {
              this.$eventBus.$emit('stop-user-login')
            })
          } else {
            this.logIn()
          }
        } else {
          this.$eventBus.$emit('stop-user-login')
        }
      })
    },
    logIn () {
      this.$refs.loginsubmit.startLoading()
      this.$store.dispatch('authUserLoginCognito')
    },
    logInMfa () {
      this.$validator.resume()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$refs.loginsubmitmfa.startLoading()
          this.$store.dispatch('authUserLoginCognitoMFA')
          this.$validator.pause()
        } else {
          this.$eventBus.$emit('stop-user-login')
        }
      })
    },
    logInNewPassword () {
      this.$validator.resume()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$refs.loginsubmitnewpassword.startLoading()
          this.$store.dispatch('authUserLoginCognitoNewPassword')
          this.$validator.pause()
        } else {
          this.$eventBus.$emit('stop-user-login')
        }
      })
    },
    logInSSO (data) {
      this.$store.dispatch('authUserLoginSAML', data)
    },
    checkUserLoginMethods () {
      return this.$store.dispatch('authUserCheckLoginMethods', this.username)
    },
    loginReset () {
      this.$store.dispatch('authUserClearLogin')
    },
    setStage (data) {
      this.$store.dispatch('authUserSetLoginStage', data)
    },
    setAuthOptions (data) {
      this.$store.dispatch('authUserSetLoginAuthOptions', data)
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/auth/login/authloginform";
</style>
